/**
 * Packaging OS — Packaging Scorecard + Vertical Pack page (Phase 28+29A).
 * POST /api/workflows/packaging-scorecard/dry-run.
 */

import { useState, type FormEvent } from "react";
import {
  DRY_RUN_CHANNELS,
  DRY_RUN_LANGUAGES,
  FRAGILITY_LEVELS,
  PRICE_BANDS,
  SHELF_ENVIRONMENTS,
  VERTICAL_PACKS,
  runPackagingScorecardDryRun,
  type ApiResult,
  type DryRunChannel,
  type DryRunLanguage,
  type FragilityLevel,
  type PackagingScorecardRequest,
  type PackagingScorecardResponse,
  type PriceBand,
  type ScorecardSection,
  type ShelfEnvironment,
  type VerticalPack,
  runWorkflowPersistedFirst,
} from "../api/client";
import { AIAssistToggle } from "../components/AIAssistToggle";
import { Button } from "../components/Button";
import { DataState, RequestIdBadge } from "../components/DataState";
import {
  FieldGrid,
  SelectField,
  TextAreaField,
  TextField,
} from "../components/FormField";
import { BulletList, ReportSection } from "../components/ReportSection";
import { RiskPill } from "../components/RiskPill";
import { strings, type UiStrings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { AppLayout } from "./CatalogLayout";

interface FormState {
  product_name: string;
  category: string;
  packaging_format: string;
  packaging_material: string;
  sales_channel: string;
  target_market: string;
  price_band: PriceBand;
  weight_grams: string;
  fragility: FragilityLevel;
  is_liquid: boolean;
  is_temperature_sensitive: boolean;
  visual_direction_notes: string;
  ecommerce_thumbnail_optimized: boolean;
  has_protective_insert: boolean;
  has_leak_proof_seal: boolean;
  has_drop_test_passed: boolean;
  shelf_environment: string;
  sustainability_claims: string;
  vertical_pack: VerticalPack;
  notes: string;
  language: DryRunLanguage;
}

const EMPTY: FormState = {
  product_name: "",
  category: "",
  packaging_format: "",
  packaging_material: "",
  sales_channel: "",
  target_market: "",
  price_band: "unknown",
  weight_grams: "",
  fragility: "none",
  is_liquid: false,
  is_temperature_sensitive: false,
  visual_direction_notes: "",
  ecommerce_thumbnail_optimized: false,
  has_protective_insert: false,
  has_leak_proof_seal: false,
  has_drop_test_passed: false,
  shelf_environment: "",
  sustainability_claims: "",
  vertical_pack: "generic",
  notes: "",
  language: "en",
};

type LoadState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "loaded"; result: ApiResult<PackagingScorecardResponse> };

interface FieldErrors {
  product_name?: string;
  category?: string;
  packaging_format?: string;
  packaging_material?: string;
  target_market?: string;
}

function splitLines(s: string, max: number): string[] {
  return s
    .split("\n")
    .map((x) => x.trim())
    .filter((x) => x.length > 0)
    .slice(0, max);
}

function bandToLevel(b: "low" | "medium" | "high"): "low" | "medium" | "high" {
  return b;
}

export function PackagingScorecardPage(): JSX.Element {
  const [locale] = useLocale();
  const t = strings(locale);
  const [form, setForm] = useState<FormState>(() => ({
    ...EMPTY,
    language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage)
      ? locale
      : "en") as DryRunLanguage,
  }));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [state, setState] = useState<LoadState>({ kind: "idle" });
  const [aiAssist, setAiAssist] = useState<boolean>(false);

  function update<K extends keyof FormState>(k: K, v: FormState[K]): void {
    setForm((p) => ({ ...p, [k]: v }));
  }

  function validate(): FieldErrors {
    const e: FieldErrors = {};
    const r = t.workflowDryRunRequiredHint;
    if (!form.product_name.trim()) e.product_name = r;
    if (!form.category.trim()) e.category = r;
    if (!form.packaging_format.trim()) e.packaging_format = r;
    if (!form.packaging_material.trim()) e.packaging_material = r;
    if (!form.target_market.trim()) e.target_market = r;
    return e;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const w = form.weight_grams.trim() ? Number(form.weight_grams) : null;
    const body: PackagingScorecardRequest = {
      product_name: form.product_name.trim(),
      category: form.category.trim(),
      packaging_format: form.packaging_format.trim(),
      packaging_material: form.packaging_material.trim(),
      sales_channel: form.sales_channel
        ? (form.sales_channel as DryRunChannel)
        : null,
      target_market: form.target_market.trim().toUpperCase(),
      price_band: form.price_band,
      weight_grams: w !== null && Number.isFinite(w) ? w : null,
      fragility: form.fragility,
      is_liquid: form.is_liquid,
      is_temperature_sensitive: form.is_temperature_sensitive,
      visual_direction_notes: form.visual_direction_notes.trim() || null,
      ecommerce_thumbnail_optimized: form.ecommerce_thumbnail_optimized,
      has_protective_insert: form.has_protective_insert,
      has_leak_proof_seal: form.has_leak_proof_seal,
      has_drop_test_passed: form.has_drop_test_passed,
      shelf_environment: form.shelf_environment
        ? (form.shelf_environment as ShelfEnvironment)
        : null,
      sustainability_claims: splitLines(form.sustainability_claims, 12),
      vertical_pack: form.vertical_pack,
      notes: form.notes.trim() || null,
      language: form.language,
      ai_assist: aiAssist,
    };
    setState({ kind: "submitting" });
    const result = await runWorkflowPersistedFirst("packaging-scorecard", body, runPackagingScorecardDryRun);
    setState({ kind: "loaded", result });
  }

  function onReset(): void {
    setForm({
      ...EMPTY,
      language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage)
        ? locale
        : "en") as DryRunLanguage,
    });
    setErrors({});
    setState({ kind: "idle" });
  }

  const opt = (vals: readonly string[]) =>
    vals.map((v) => ({ value: v, label: v }));

  const yesNo = [
    { value: "no", label: t.reportReviewRequiredNo },
    { value: "yes", label: t.reportReviewRequiredYes },
  ];

  return (
    <AppLayout>
      <header className="po-section-header">
        <span className="po-section-header__eyebrow">
          {t.appShellNavWorkflows}
        </span>
        <h1 className="po-section-header__title">{t.scorecardTitle}</h1>
        <p className="po-note">{t.scorecardSubtitle}</p>
      </header>

      <section className="po-card">
        <form className="po-form" onSubmit={onSubmit} noValidate>
          <FieldGrid>
            <TextField
              label={t.intakeFieldProductName}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.product_name}
              onChange={(v) => update("product_name", v)}
              error={errors.product_name}
              maxLength={200}
            />
            <TextField
              label={t.intakeFieldProductCategory}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.category}
              onChange={(v) => update("category", v)}
              error={errors.category}
              maxLength={100}
            />
            <TextField
              label={t.supplierFieldFormat}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.packaging_format}
              onChange={(v) => update("packaging_format", v)}
              error={errors.packaging_format}
              maxLength={120}
            />
            <TextField
              label={t.supplierFieldMaterial}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.packaging_material}
              onChange={(v) => update("packaging_material", v)}
              error={errors.packaging_material}
              maxLength={120}
            />
            <TextField
              label={t.labelCopyFieldTargetMarket}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.target_market}
              onChange={(v) => update("target_market", v.toUpperCase())}
              error={errors.target_market}
              maxLength={8}
            />
            <SelectField
              label={t.scorecardFieldVerticalPack}
              value={form.vertical_pack}
              onChange={(v) => update("vertical_pack", v as VerticalPack)}
              options={opt(VERTICAL_PACKS)}
            />
            <SelectField
              label={t.labelCopyFieldSalesChannel}
              value={form.sales_channel}
              onChange={(v) => update("sales_channel", v)}
              options={opt(DRY_RUN_CHANNELS)}
              emptyLabel={t.workflowDryRunNotSelected}
            />
            <SelectField
              label={t.intakeFieldPriceBand}
              value={form.price_band}
              onChange={(v) => update("price_band", v as PriceBand)}
              options={opt(PRICE_BANDS)}
            />
            <SelectField
              label={t.scorecardFieldShelfEnv}
              value={form.shelf_environment}
              onChange={(v) => update("shelf_environment", v)}
              options={opt(SHELF_ENVIRONMENTS)}
              emptyLabel={t.workflowDryRunNotSelected}
            />
            <SelectField
              label={t.scorecardFieldFragility}
              value={form.fragility}
              onChange={(v) => update("fragility", v as FragilityLevel)}
              options={opt(FRAGILITY_LEVELS)}
            />
            <TextField
              label={t.intakeFieldSizeValue}
              value={form.weight_grams}
              onChange={(v) => update("weight_grams", v)}
            />
            <SelectField
              label={t.intakeFieldLanguage}
              value={form.language}
              onChange={(v) => update("language", v as DryRunLanguage)}
              options={opt(DRY_RUN_LANGUAGES)}
            />
            <SelectField
              label={t.scorecardFieldLiquid}
              value={form.is_liquid ? "yes" : "no"}
              onChange={(v) => update("is_liquid", v === "yes")}
              options={yesNo}
            />
            <SelectField
              label={t.scorecardFieldTempSensitive}
              value={form.is_temperature_sensitive ? "yes" : "no"}
              onChange={(v) =>
                update("is_temperature_sensitive", v === "yes")
              }
              options={yesNo}
            />
            <SelectField
              label={t.scorecardFieldThumbnailOpt}
              value={form.ecommerce_thumbnail_optimized ? "yes" : "no"}
              onChange={(v) =>
                update("ecommerce_thumbnail_optimized", v === "yes")
              }
              options={yesNo}
            />
            <SelectField
              label={t.scorecardFieldProtectiveInsert}
              value={form.has_protective_insert ? "yes" : "no"}
              onChange={(v) =>
                update("has_protective_insert", v === "yes")
              }
              options={yesNo}
            />
            <SelectField
              label={t.scorecardFieldLeakSeal}
              value={form.has_leak_proof_seal ? "yes" : "no"}
              onChange={(v) =>
                update("has_leak_proof_seal", v === "yes")
              }
              options={yesNo}
            />
            <SelectField
              label={t.scorecardFieldDropTest}
              value={form.has_drop_test_passed ? "yes" : "no"}
              onChange={(v) =>
                update("has_drop_test_passed", v === "yes")
              }
              options={yesNo}
            />
            <TextAreaField
              label={t.scorecardFieldVisualNotes}
              value={form.visual_direction_notes}
              onChange={(v) => update("visual_direction_notes", v)}
              rows={2}
              maxLength={500}
              full
            />
            <TextAreaField
              label={t.sustFieldClaims}
              hint={t.sustFieldClaimsHelp}
              value={form.sustainability_claims}
              onChange={(v) => update("sustainability_claims", v)}
              rows={2}
              full
            />
            <TextAreaField
              label={t.intakeFieldNotes}
              value={form.notes}
              onChange={(v) => update("notes", v)}
              rows={2}
              maxLength={1000}
              full
            />
          </FieldGrid>

          {Object.keys(errors).length > 0 && (
            <p className="po-field__error">
              {t.workflowDryRunValidationFailed}
            </p>
          )}

          <AIAssistToggle
            checked={aiAssist}
            onChange={setAiAssist}
            label={t.aiAssistToggleLabel}
            hint={t.aiAssistToggleHint}
            banner={t.aiAssistDraftBanner}
            disabled={state.kind === "submitting"}
          />

          <div className="po-row">
            <Button
              variant="primary"
              type="submit"
              disabled={state.kind === "submitting"}
            >
              {state.kind === "submitting"
                ? t.scorecardSubmitting
                : t.scorecardSubmit}
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={onReset}
              disabled={state.kind === "submitting"}
            >
              {t.scorecardReset}
            </Button>
          </div>
        </form>
      </section>

      {state.kind === "loaded" && (
        <DataState
          state={{ kind: "loaded", result: state.result }}
          t={t}
          renderOk={(data, requestId) => (
            <ScorecardReport data={data} requestId={requestId} t={t} />
          )}
        />
      )}
    </AppLayout>
  );
}

interface ScorecardReportProps {
  data: PackagingScorecardResponse;
  requestId: string | null;
  t: UiStrings;
}

function ScorecardReport({
  data,
  requestId,
  t,
}: ScorecardReportProps): JSX.Element {
  const overallLabel =
    data.overall_band === "high"
      ? t.scorecardBandHigh
      : data.overall_band === "medium"
        ? t.scorecardBandMedium
        : t.scorecardBandLow;

  const sections: Array<[string, ScorecardSection]> = [
    [t.scorecardSectionShelf, data.shelf_visibility_score],
    [t.scorecardSectionEcommerce, data.ecommerce_thumbnail_score],
    [t.scorecardSectionDelivery, data.delivery_durability_score],
    [t.scorecardSectionUnboxing, data.unboxing_experience_score],
    [t.scorecardSectionMarketplace, data.marketplace_readiness_score],
  ];

  return (
    <div className="po-stack" data-testid="po-scorecard-report">
      <ReportSection title={t.scorecardOverall}>
        <p className="po-row">
          <span className="po-request-id">{data.overall_score} / 100</span>
          <RiskPill
            level={bandToLevel(data.overall_band)}
            label={overallLabel}
          />
        </p>
      </ReportSection>
      <ReportSection title={t.scorecardVerticalPackLabel}>
        <p className="po-note">
          <strong>
            <span className="dir-ltr-island">{data.vertical_pack.code}</span>:
          </strong>{" "}
          {data.vertical_pack.lead}
        </p>
        {data.vertical_pack.emphasis.length > 0 && (
          <p className="po-note po-note--small">
            <span className="dir-ltr-island">
              {data.vertical_pack.emphasis.join(", ")}
            </span>
          </p>
        )}
      </ReportSection>

      <div className="po-table-wrap">
        <table className="po-table">
          <thead>
            <tr>
              <th scope="col">{t.intakeFieldProductCategory}</th>
              <th scope="col">{t.scorecardOverall}</th>
              <th scope="col">{t.scorecardBandHigh}</th>
              <th scope="col">{t.reportClaimRiskReviewReasons}</th>
            </tr>
          </thead>
          <tbody>
            {sections.map(([label, sec]) => (
              <tr key={label}>
                <td>{label}</td>
                <td>
                  <span className="po-request-id">{sec.score}</span>
                </td>
                <td>
                  <RiskPill
                    level={bandToLevel(sec.band)}
                    label={sec.band_label}
                  />
                </td>
                <td>
                  <ul className="po-list">
                    {sec.rationale.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ReportSection title={t.scorecardRiskFlags}>
        <BulletList items={data.risk_flags} empty={t.reportNoFlags} />
      </ReportSection>
      <ReportSection title={t.scorecardImprovements}>
        <BulletList items={data.improvement_suggestions} />
      </ReportSection>
      <ReportSection title={t.scorecardChannelRecs}>
        <BulletList
          items={data.channel_specific_recommendations}
          empty={t.reportNoFlags}
        />
      </ReportSection>
      <ReportSection title={t.scorecardMissing}>
        <BulletList
          items={data.missing_information}
          empty={t.reportNoFlags}
        />
      </ReportSection>
      <ReportSection title={t.intakeReportHumanReview}>
        <BulletList items={data.human_review_required_notes} />
      </ReportSection>
      {requestId && <RequestIdBadge t={t} requestId={requestId} />}
    </div>
  );
}
